import { useState, useEffect } from "react";
import { Link, useSearchParams } from "react-router-dom";
import Header from "../components/Header";
import Footer from "../components/Footer";
import { Mic, Search, FileSearch, Settings, Lock, Sparkles, Loader2, Volume2, Shield, AlertCircle, Plus, Check } from "lucide-react";
import { http } from "../lib/api";
import { useAuth } from "../auth/AuthContext";
import PricingModal from "../components/PricingModal";

const READER_TIERS = ["basic", "lite", "pro", "channels", "elite", "business"];
const ELITE_TIERS = ["channels", "elite", "business"];

export default function ReaderTools() {
  const { user, token } = useAuth();
  const [searchParams] = useSearchParams();
  const initialTab = searchParams.get("tab") || "paste";
  const [tab, setTab] = useState(initialTab);
  const [showPricing, setShowPricing] = useState(false);

  const tier = user?.tier || "free";
  const isReaderOrUp = READER_TIERS.includes(tier);
  const isEliteOrUp = ELITE_TIERS.includes(tier);

  const tabs = [
    { id: "paste", label: "Universal Scorer", icon: Search, requires: "free", description: "Paste any URL — news article, X/Twitter post, YouTube video, Reddit thread, blog, podcast — Newseze scores it for trust, drama, conspiracy, respect, and bias in our calm voice." },
    { id: "investigator", label: "My Investigator", icon: FileSearch, requires: "elite", description: "Submit a subject. Newseze builds a full investigative piece with timeline, images, and ongoing tracking." },
    { id: "custom", label: "Custom Feed", icon: Settings, requires: "reader", description: "Tailor your news to your subjects, regions, and topics. Stored server-side; syncs across devices." },
    { id: "read", label: "Read Aloud Demo", icon: Mic, requires: null, description: "Try the avatar-narrated story reader." },
  ];

  return (
    <>
      <Header />
      <main className="max-w-[1400px] mx-auto px-4 sm:px-6 py-6">
        <div className="mb-6">
          <div className="text-[10px] uppercase tracking-[0.2em] font-black text-[var(--gold)] mb-1 flex items-center gap-2">
            <Sparkles size={12} /> Reader Tools
          </div>
          <h1 className="text-[28px] sm:text-[36px] font-black tracking-[-0.02em] leading-tight">Your personal newsroom.</h1>
          <p className="text-[13.5px] text-[var(--muted)] mt-2 max-w-[720px] leading-snug">
            Paste any story, build an investigation, customize your feed, and listen to Newseze read it to you.
            Premium features are gated by tier — upgrade to unlock more.
          </p>
        </div>

        {/* Tab bar */}
        <div className="flex flex-wrap gap-2 mb-5 border-b border-[var(--line)]" data-testid="reader-tab-bar">
          {tabs.map((t) => (
            <button key={t.id} onClick={() => setTab(t.id)} className={`px-4 py-2 -mb-px border-b-2 text-[12px] uppercase tracking-[0.14em] font-black transition flex items-center gap-2 ${tab === t.id ? "border-[var(--gold)] text-[var(--ink)]" : "border-transparent text-[var(--muted)] hover:text-[var(--ink)]"}`} data-testid={`reader-tab-${t.id}`}>
              <t.icon size={13} />{t.label}
            </button>
          ))}
        </div>

        <div className="mb-4 text-[13px] text-[var(--muted)] italic" data-testid="reader-tab-desc">
          {tabs.find((t) => t.id === tab)?.description}
        </div>

        {tab === "paste" && (
          <PasteTakeTool isReaderOrUp={isReaderOrUp} token={token} onUpgrade={() => setShowPricing(true)} />
        )}
        {tab === "investigator" && (
          <InvestigatorTool isEliteOrUp={isEliteOrUp} token={token} onUpgrade={() => setShowPricing(true)} />
        )}
        {tab === "custom" && (
          <CustomFeedTool isReaderOrUp={isReaderOrUp} token={token} onUpgrade={() => setShowPricing(true)} />
        )}
        {tab === "read" && (
          <ReadAloudDemo token={token} isReaderOrUp={isReaderOrUp} />
        )}

      </main>
      <Footer />
      {showPricing && <PricingModal onClose={() => setShowPricing(false)} />}
    </>
  );
}

// ============ Universal Scorer (paste any URL) ============
const MEDIA_LABELS = {
  twitter: "X / Twitter", youtube: "YouTube", reddit: "Reddit", tiktok: "TikTok",
  facebook: "Facebook", instagram: "Instagram", threads: "Threads", bluesky: "Bluesky",
  linkedin: "LinkedIn", podcast: "Podcast", substack: "Substack", blog: "Blog",
  news: "News article", other: "Web page", text: "Text",
};

function PasteTakeTool({ isReaderOrUp, token, onUpgrade }) {
  const [url, setUrl] = useState("");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [err, setErr] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true); setErr(""); setResult(null);
    try {
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const r = await http.post("/reader/paste-analyze", { url: url.trim() }, { headers });
      setResult(r.data);
    } catch (ex) {
      const detail = ex?.response?.data?.detail || "Could not analyze. Try a different URL.";
      setErr(detail);
      if (/limit reached|upgrade/i.test(detail) && onUpgrade) {
        // Surface upgrade CTA in the error block
      }
    } finally { setBusy(false); }
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-[1fr_400px] gap-5" data-testid="paste-tool">
      <div>
        <form onSubmit={submit} className="rounded-xl border border-[var(--line)] bg-white p-5 mb-4">
          <label className="text-[11px] uppercase tracking-[0.16em] font-black text-[var(--blue)] block mb-2">Paste any URL — news, X post, YouTube, Reddit, blog, podcast</label>
          <div className="flex gap-2">
            <input
              type="url"
              value={url}
              onChange={(e) => setUrl(e.target.value)}
              placeholder="https://x.com/… · youtube.com/watch?v=… · reuters.com/… · reddit.com/r/…"
              className="flex-1 border border-[var(--line)] rounded-md px-3 py-2.5 text-[13px]"
              required
              data-testid="paste-url"
            />
            <button type="submit" disabled={busy} className="btn primary" data-testid="paste-submit">
              {busy ? <Loader2 size={14} className="animate-spin" /> : <Search size={14} />}
              {busy ? "Scoring…" : "Score it"}
            </button>
          </div>
          <div className="text-[11.5px] text-[var(--muted)] mt-2 italic">
            Free: 1 score per day · Reader ($6.99): 10/day · Pro: 30/day · Channels & Elite: unlimited.
          </div>
          {err && (
            <div className="mt-3 text-[12px] text-rose-700 flex flex-col gap-1.5" data-testid="paste-error">
              <span>{err}</span>
              {/limit reached|upgrade/i.test(err) && (
                <button onClick={onUpgrade} className="text-[var(--blue)] font-black underline self-start" data-testid="paste-upgrade-cta">See plans →</button>
              )}
            </div>
          )}
        </form>

        {result && (
          <article className="rounded-xl border border-[var(--gold)] bg-white p-6" data-testid="paste-result">
            <div className="flex items-center gap-2 mb-2">
              <div className="text-[10px] uppercase tracking-[0.18em] font-black text-[var(--gold)]">Newseze Score</div>
              {result.media_type && result.media_type !== "text" && (
                <span className="text-[9.5px] uppercase tracking-[0.14em] font-black bg-[#fff9ed] text-[#7a5b00] border border-[var(--gold)] px-1.5 py-0.5 rounded" data-testid="paste-media-type">
                  {MEDIA_LABELS[result.media_type] || result.media_type}
                </span>
              )}
            </div>
            <h2 className="text-[24px] font-black tracking-[-0.02em] leading-tight mb-2">{result.newseze_title}</h2>
            {result.blocked ? (
              <div className="rounded-md bg-rose-50 border border-rose-200 p-3 mb-3 text-[12.5px] text-rose-800 flex gap-2 items-start" data-testid="paste-blocked">
                <AlertCircle size={14} className="shrink-0 mt-0.5" />
                <div>
                  <b>Newseze would not republish this.</b>
                  <div>{result.block_reason || "Failed calm filters."}</div>
                </div>
              </div>
            ) : null}
            {result.why_it_matters && (
              <p className="text-[14px] font-bold text-[var(--ink)] mb-3 leading-snug border-l-4 border-[var(--gold)] pl-3 italic">{result.why_it_matters}</p>
            )}
            <div className="prose prose-sm max-w-none text-[13.5px] leading-relaxed text-[var(--ink)]" style={{ whiteSpace: "pre-wrap" }}>
              {result.narrative}
            </div>
            {result.source_url && (
              <div className="mt-4 pt-3 border-t border-[var(--line)] text-[11px] text-[var(--muted)] italic">
                Source URL: <a href={result.source_url} target="_blank" rel="nofollow noopener noreferrer" referrerPolicy="no-referrer" className="text-[var(--blue)] underline break-all">{result.source_url}</a>
              </div>
            )}
            {result.share_id && (
              <div className="mt-3 pt-3 border-t border-[var(--line)] flex flex-wrap items-center gap-2" data-testid="paste-share-row">
                <span className="text-[10px] uppercase tracking-[0.18em] font-black text-[var(--muted)] mr-1">Share this score:</span>
                <Link to={`/scored/${result.share_id}`} className="btn icon" data-testid="paste-share-permalink">
                  Permalink →
                </Link>
                <a
                  href={`https://twitter.com/intent/tweet?text=${encodeURIComponent("Newseze scored this — Trust " + result.trust_score + " · Drama " + result.drama_score)}&url=${encodeURIComponent(`${(process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "")}/api/share/scored/${result.share_id}`)}`}
                  target="_blank" rel="nofollow noopener noreferrer" referrerPolicy="no-referrer"
                  className="btn icon" data-testid="paste-share-x"
                >Share on X</a>
                <a
                  href={`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(`${(process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "")}/api/share/scored/${result.share_id}`)}`}
                  target="_blank" rel="nofollow noopener noreferrer" referrerPolicy="no-referrer"
                  className="btn icon" data-testid="paste-share-fb"
                >Share on FB</a>
              </div>
            )}
          </article>
        )}
      </div>

      {/* Scores sidebar */}
      <aside>
        {result && (
          <div className="rounded-xl border border-[var(--line)] bg-white p-5 sticky top-4" data-testid="paste-scores">
            <div className="flex items-center gap-2 mb-3"><Shield size={14} className="text-[var(--gold)]" /><div className="text-[11px] uppercase tracking-[0.18em] font-black text-[var(--blue)]">Editorial Scores</div></div>
            <ScoreRow label="Trust" value={result.trust_score} max={100} good />
            <ScoreRow label="Respect" value={result.respect_score} max={100} good />
            <ScoreRow label="Drama" value={result.drama_score} max={100} good={false} />
            <ScoreRow label="Conspiracy" value={result.conspiracy_score} max={100} good={false} />
            <div className="mt-3 pt-3 border-t border-[var(--line)]">
              <div className="text-[10.5px] uppercase tracking-[0.16em] font-black text-[var(--muted)]">Bias Score</div>
              <div className="text-[20px] font-black tabular-nums">{result.bias_score > 0 ? `+${result.bias_score}` : result.bias_score}</div>
              <div className="text-[10px] text-[var(--muted)] italic">-100 left · 0 center · +100 right</div>
            </div>
            {result.quota && (
              <div className="mt-3 pt-3 border-t border-[var(--line)] text-[10.5px] text-[var(--muted)]">
                Today: <b className="text-[var(--ink)]">{result.quota.used}/{result.quota.cap === 9999 ? "∞" : result.quota.cap}</b> · <span className="uppercase tracking-[0.12em] font-black">{result.quota.tier}</span>
              </div>
            )}
          </div>
        )}
      </aside>
    </div>
  );
}

function ScoreRow({ label, value, max = 100, good = true }) {
  const pct = Math.max(0, Math.min(100, (value / max) * 100));
  const color = good ? (pct >= 70 ? "bg-emerald-500" : pct >= 40 ? "bg-amber-500" : "bg-rose-500") : (pct >= 70 ? "bg-rose-500" : pct >= 40 ? "bg-amber-500" : "bg-emerald-500");
  return (
    <div className="mb-2">
      <div className="flex justify-between text-[11px]"><span className="text-[var(--muted)]">{label}</span><b className="tabular-nums">{value}</b></div>
      <div className="h-1.5 bg-[var(--bg)] rounded-full overflow-hidden mt-0.5"><div className={`h-full ${color}`} style={{ width: `${pct}%` }} /></div>
    </div>
  );
}

// ============ My Investigator ============
function InvestigatorTool({ isEliteOrUp, token, onUpgrade }) {
  const [subject, setSubject] = useState("");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [list, setList] = useState([]);
  const [err, setErr] = useState("");
  const [selected, setSelected] = useState(null);

  useEffect(() => {
    if (!token) return;
    http.get("/reader/investigator/list", { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => setList(r.data?.items || []))
      .catch(() => setList([]));
  }, [token, result]);

  const submit = async (e) => {
    e.preventDefault();
    if (!isEliteOrUp) { onUpgrade(); return; }
    setBusy(true); setErr(""); setResult(null);
    try {
      const r = await http.post("/reader/investigator", { subject }, { headers: { Authorization: `Bearer ${token}` } });
      setResult(r.data);
      setSelected(r.data);
    } catch (ex) {
      setErr(ex?.response?.data?.detail || "Could not start investigation.");
    } finally { setBusy(false); }
  };

  const loadOne = async (id) => {
    const r = await http.get(`/reader/investigator/${id}`, { headers: { Authorization: `Bearer ${token}` } });
    setSelected(r.data);
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-[280px_1fr] gap-5" data-testid="investigator-tool">
      <aside className="space-y-3">
        <form onSubmit={submit} className="rounded-xl border border-[var(--line)] bg-white p-4">
          <label className="text-[11px] uppercase tracking-[0.16em] font-black text-[var(--blue)] block mb-2">Subject</label>
          <input
            value={subject}
            onChange={(e) => setSubject(e.target.value)}
            placeholder="e.g., Federal AI regulation 2026"
            className="w-full border border-[var(--line)] rounded-md px-3 py-2.5 text-[13px] mb-2"
            required
            minLength={8}
            data-testid="investigator-subject"
          />
          <button type="submit" disabled={busy || !subject.trim()} className={`btn ${isEliteOrUp ? "gold" : ""} w-full justify-center`} data-testid="investigator-submit">
            {busy ? <Loader2 size={14} className="animate-spin" /> : isEliteOrUp ? <FileSearch size={14} /> : <Lock size={14} />}
            {busy ? "Investigating…" : isEliteOrUp ? "Start Investigation" : "Channel Builder ($19.99)+"}
          </button>
          {!isEliteOrUp && <div className="text-[11px] text-[var(--muted)] mt-2 italic">5 investigations per day.</div>}
          {err && <div className="text-[12px] text-rose-700 mt-2">{err}</div>}
        </form>
        {list.length > 0 && (
          <div className="rounded-xl border border-[var(--line)] bg-white p-3" data-testid="investigator-list">
            <div className="text-[10px] uppercase tracking-[0.16em] font-black text-[var(--muted)] mb-2">My investigations</div>
            {list.map((inv) => (
              <button key={inv.id} onClick={() => loadOne(inv.id)} className={`w-full text-left p-2 rounded mb-1 border ${selected?.id === inv.id ? "border-[var(--gold)] bg-[#fff9ed]" : "border-transparent hover:bg-[var(--bg)]"}`} data-testid={`investigator-item-${inv.id}`}>
                <div className="text-[12px] font-black line-clamp-2">{inv.headline || inv.subject}</div>
                <div className="text-[10px] text-[var(--muted)] mt-0.5">Trust {inv.trust_score} · {inv.update_count} updates</div>
              </button>
            ))}
          </div>
        )}
      </aside>

      {selected ? <InvestigationView inv={selected} token={token} onUpdated={(u) => setSelected(u)} /> : <div className="text-[13px] text-[var(--muted)] italic p-6">Submit a subject to generate your first investigation.</div>}
    </div>
  );
}

function InvestigationView({ inv, token, onUpdated }) {
  const [refreshing, setRefreshing] = useState(false);
  const [refreshMsg, setRefreshMsg] = useState("");
  const refresh = async () => {
    setRefreshing(true); setRefreshMsg("");
    try {
      const r = await http.post(`/reader/investigator/${inv.id}/refresh`, null, { headers: { Authorization: `Bearer ${token}` } });
      if (r.data?.ok) {
        const added = r.data.added || 0;
        setRefreshMsg(added > 0 ? `Added ${added} new update${added > 1 ? "s" : ""}` : "No fresh coverage in last 24h");
        onUpdated?.({ ...inv, updates: r.data.updates, updated_at: r.data.updated_at });
      } else if (r.data?.reason === "rate_limited") {
        setRefreshMsg(`Try again in ${r.data.wait_seconds || 60}s`);
      } else if (r.data?.reason === "no_keywords") {
        setRefreshMsg("No keywords to track");
      }
    } catch (ex) {
      setRefreshMsg(ex?.response?.data?.detail || "Refresh failed");
    } finally {
      setRefreshing(false);
      setTimeout(() => setRefreshMsg(""), 4500);
    }
  };
  return (
    <article className="rounded-xl border border-[var(--gold)] bg-white p-6" data-testid={`investigation-${inv.id}`}>
      <div className="flex items-start justify-between gap-3 mb-1">
        <div className="text-[10px] uppercase tracking-[0.18em] font-black text-[var(--gold)]">Newseze Investigation</div>
        {token && (
          <button onClick={refresh} disabled={refreshing} className="btn icon text-[11px]" data-testid="investigation-refresh">
            {refreshing ? <Loader2 size={12} className="animate-spin" /> : <Sparkles size={12} />} Refresh updates
          </button>
        )}
      </div>
      {refreshMsg && <div className="text-[11px] text-[var(--muted)] italic mb-2" data-testid="investigation-refresh-msg">{refreshMsg}</div>}
      <h2 className="text-[28px] font-black tracking-[-0.02em] leading-tight mb-2">{inv.headline}</h2>
      {inv.deck && <p className="text-[15px] text-[var(--muted)] italic mb-4 leading-snug">{inv.deck}</p>}
      {inv.image_b64 && (
        <img src={`data:image/png;base64,${inv.image_b64}`} alt={inv.headline} className="w-full rounded-lg mb-4" data-testid="investigation-image" />
      )}
      {(inv.key_findings || []).length > 0 && (
        <div className="rounded-md bg-[#fff9ed] border border-[var(--gold)] p-4 mb-5">
          <div className="text-[10px] uppercase tracking-[0.18em] font-black text-[#7a5b00] mb-2">Key findings</div>
          <ul className="text-[13.5px] list-disc pl-5 space-y-1">{inv.key_findings.map((f, i) => <li key={i}>{f}</li>)}</ul>
        </div>
      )}
      <div className="prose prose-sm max-w-none text-[14px] leading-relaxed mb-5" style={{ whiteSpace: "pre-wrap" }}>{inv.body}</div>
      {(inv.timeline || []).length > 0 && (
        <section className="mb-5">
          <h3 className="text-[12px] uppercase tracking-[0.18em] font-black text-[var(--blue)] mb-2">Timeline</h3>
          <ol className="border-l-2 border-[var(--gold)] ml-2 pl-4 space-y-2">
            {inv.timeline.map((t, i) => (
              <li key={i} className="text-[13px]"><b className="text-[var(--blue)]">{t.date}:</b> {t.event}</li>
            ))}
          </ol>
        </section>
      )}
      {(inv.unanswered_questions || []).length > 0 && (
        <section className="mb-5 rounded-md bg-rose-50 border border-rose-200 p-4">
          <h3 className="text-[11px] uppercase tracking-[0.18em] font-black text-rose-700 mb-2">Unanswered questions</h3>
          <ul className="text-[13px] list-disc pl-5 space-y-1">{inv.unanswered_questions.map((q, i) => <li key={i}>{q}</li>)}</ul>
        </section>
      )}
      {(inv.updates || []).length > 0 && (
        <section className="mb-5">
          <h3 className="text-[12px] uppercase tracking-[0.18em] font-black text-[var(--blue)] mb-2">Ongoing reporting · {inv.updates.length} updates</h3>
          <ul className="space-y-2">
            {inv.updates.map((u) => (
              <li key={u.article_id} className="text-[12.5px] border-l-2 border-[var(--gold)] pl-3">
                <a href={`/article/${u.article_id}`} className="hover:text-[var(--blue)]">
                  <div className="font-black">{u.title}</div>
                  <div className="text-[10px] text-[var(--muted)] uppercase tracking-[0.12em]">{u.source} · added {u.added_at?.slice(0, 10)}</div>
                </a>
              </li>
            ))}
          </ul>
        </section>
      )}
      {(inv.related_videos || []).length > 0 && (
        <section className="mb-5">
          <h3 className="text-[12px] uppercase tracking-[0.18em] font-black text-[var(--blue)] mb-2">Related video coverage</h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3" data-testid="investigation-videos">
            {inv.related_videos.map((v) => (
              <a key={v.id} href={v.link} target="_blank" rel="nofollow noopener noreferrer" referrerPolicy="no-referrer" className="block rounded-lg border border-[var(--line)] overflow-hidden hover:border-[var(--gold)] hover:shadow-md transition group" data-testid={`investigation-video-${v.id}`}>
                {v.thumbnail && (
                  <div className="aspect-video bg-cover bg-center" style={{ backgroundImage: `url(${v.thumbnail})` }} />
                )}
                <div className="p-2.5">
                  <div className="text-[10px] uppercase tracking-[0.14em] font-black text-[var(--gold)] mb-0.5">{v.source}</div>
                  <div className="text-[12.5px] font-bold line-clamp-2 group-hover:text-[var(--blue)] leading-snug">{v.title}</div>
                </div>
              </a>
            ))}
          </div>
        </section>
      )}
      {(inv.related_articles || []).length > 0 && (
        <section className="mb-2">
          <h3 className="text-[12px] uppercase tracking-[0.18em] font-black text-[var(--blue)] mb-2">Related wire coverage</h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            {inv.related_articles.map((r) => (
              <a key={r.id} href={`/article/${r.id}`} className="block rounded border border-[var(--line)] p-2 hover:border-[var(--gold)] text-[12px]">
                <div className="text-[9.5px] uppercase tracking-[0.12em] text-[var(--muted)] font-bold">WIRE: {r.source}</div>
                <div className="font-black line-clamp-2 mt-0.5">{r.title}</div>
              </a>
            ))}
          </div>
        </section>
      )}
    </article>
  );
}

// ============ Custom Feed editor ============
const ALL_CATS = ["local","national","world","government","economy","markets","sports","technology","health","entertainment","crime","investigations","family","culture"];
function CustomFeedTool({ isReaderOrUp, token, onUpgrade }) {
  const [prefs, setPrefs] = useState({ categories: [], regions: [], topics: [], blocked_topics: [], avoid_outlets: [] });
  const [saved, setSaved] = useState(false);
  const [busy, setBusy] = useState(false);
  const [topicInput, setTopicInput] = useState("");
  const [blockedInput, setBlockedInput] = useState("");
  const [outletInput, setOutletInput] = useState("");

  useEffect(() => {
    if (!token) return;
    http.get("/reader/custom-feed", { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => { if (r.data?.prefs) setPrefs({ ...prefs, ...r.data.prefs }); })
      .catch(() => {});
    // eslint-disable-next-line
  }, [token]);

  const toggleCat = (c) => setPrefs((p) => ({ ...p, categories: p.categories.includes(c) ? p.categories.filter((x) => x !== c) : [...p.categories, c] }));
  const addTag = (key, input, setter) => {
    const v = input.trim();
    if (!v) return;
    setPrefs((p) => ({ ...p, [key]: [...new Set([...(p[key] || []), v])] }));
    setter("");
  };
  const removeTag = (key, val) => setPrefs((p) => ({ ...p, [key]: (p[key] || []).filter((x) => x !== val) }));

  const save = async (e) => {
    e?.preventDefault?.();
    if (!isReaderOrUp) { onUpgrade(); return; }
    setBusy(true); setSaved(false);
    try {
      await http.put("/reader/custom-feed", prefs, { headers: { Authorization: `Bearer ${token}` } });
      setSaved(true);
      setTimeout(() => setSaved(false), 2500);
    } finally { setBusy(false); }
  };

  return (
    <form onSubmit={save} className="grid grid-cols-1 lg:grid-cols-2 gap-5" data-testid="custom-feed-tool">
      <div className="space-y-5">
        <div className="rounded-xl border border-[var(--line)] bg-white p-4">
          <div className="text-[11px] uppercase tracking-[0.16em] font-black text-[var(--blue)] mb-3">Favorite subjects</div>
          <div className="flex flex-wrap gap-2">
            {ALL_CATS.map((c) => (
              <button type="button" key={c} onClick={() => toggleCat(c)} className={`px-3 py-1 rounded-full text-[12px] font-bold transition ${prefs.categories.includes(c) ? "bg-[var(--ink)] text-white" : "bg-[var(--bg)] text-[var(--muted)] hover:text-[var(--ink)]"}`} data-testid={`pref-cat-${c}`}>
                {c}
              </button>
            ))}
          </div>
        </div>
        <TagInput title="Topics to follow" placeholder="e.g., quantum computing, watch auctions" value={topicInput} setValue={setTopicInput} tags={prefs.topics} onAdd={() => addTag("topics", topicInput, setTopicInput)} onRemove={(v) => removeTag("topics", v)} testid="topics" />
        <TagInput title="Regions / locations" placeholder="e.g., Phoenix, Texas, Europe" value={""} setValue={() => {}} tags={prefs.regions} onAdd={(v) => addTag("regions", v, () => {})} onRemove={(v) => removeTag("regions", v)} testid="regions" controlled />
      </div>
      <div className="space-y-5">
        <TagInput title="Block topics (you don't want to see)" placeholder="e.g., celebrity gossip, royal family" value={blockedInput} setValue={setBlockedInput} tags={prefs.blocked_topics} onAdd={() => addTag("blocked_topics", blockedInput, setBlockedInput)} onRemove={(v) => removeTag("blocked_topics", v)} testid="blocked" />
        <TagInput title="Avoid these outlets" placeholder="e.g., TMZ, Daily Mail" value={outletInput} setValue={setOutletInput} tags={prefs.avoid_outlets} onAdd={() => addTag("avoid_outlets", outletInput, setOutletInput)} onRemove={(v) => removeTag("avoid_outlets", v)} testid="outlets" />
        <button type="submit" disabled={busy} className={`btn ${isReaderOrUp ? "primary" : "gold"} w-full justify-center`} data-testid="custom-feed-save">
          {busy ? <Loader2 size={14} className="animate-spin" /> : isReaderOrUp ? <Check size={14} /> : <Lock size={14} />}
          {busy ? "Saving…" : saved ? "Saved ✓" : isReaderOrUp ? "Save preferences" : "Reader ($6.99)+ required"}
        </button>
        {isReaderOrUp && (
          <Link to="/section/personalized" className="text-[12.5px] text-[var(--blue)] font-bold underline block text-center">→ View my custom feed</Link>
        )}
      </div>
    </form>
  );
}

function TagInput({ title, placeholder, value, setValue, tags, onAdd, onRemove, testid }) {
  const [local, setLocal] = useState("");
  const v = value === undefined ? local : value;
  const setV = setValue === undefined ? setLocal : setValue;
  const handleAdd = () => {
    if (typeof onAdd === "function") onAdd(v);
    setLocal("");
  };
  return (
    <div className="rounded-xl border border-[var(--line)] bg-white p-4">
      <div className="text-[11px] uppercase tracking-[0.16em] font-black text-[var(--blue)] mb-2">{title}</div>
      <div className="flex gap-2 mb-2">
        <input value={v} onChange={(e) => setV(e.target.value)} placeholder={placeholder} className="flex-1 border border-[var(--line)] rounded-md px-3 py-2 text-[13px]" data-testid={`tag-input-${testid}`} onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); handleAdd(); } }} />
        <button type="button" onClick={handleAdd} className="btn icon" data-testid={`tag-add-${testid}`}><Plus size={13} /></button>
      </div>
      <div className="flex flex-wrap gap-1.5">
        {(tags || []).map((t) => (
          <span key={t} className="bg-[var(--bg)] text-[var(--ink)] rounded-full text-[11.5px] px-2.5 py-0.5 font-bold inline-flex items-center gap-1" data-testid={`tag-${testid}-${t}`}>
            {t}
            <button type="button" onClick={() => onRemove(t)} className="text-[var(--muted)] hover:text-[var(--ink)]" aria-label="Remove">×</button>
          </span>
        ))}
      </div>
    </div>
  );
}

// ============ Read-Aloud Demo ============
function ReadAloudDemo({ token, isReaderOrUp }) {
  const [recent, setRecent] = useState([]);
  useEffect(() => {
    http.get("/news?limit=4").then((r) => setRecent(r.data || []));
  }, []);
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-3" data-testid="read-aloud-demo">
      {recent.map((a) => (
        <div key={a.id} className="rounded-xl border border-[var(--line)] bg-white p-4 flex flex-col gap-2">
          <div className="text-[15px] font-black line-clamp-2 leading-snug">{a.title}</div>
          <div className="text-[11px] text-[var(--muted)] uppercase tracking-[0.12em] font-bold">WIRE: {a.source}</div>
          <ReadAloudButton articleId={a.id} token={token} isReaderOrUp={isReaderOrUp} />
        </div>
      ))}
    </div>
  );
}

export function ReadAloudButton({ articleId, token, isReaderOrUp }) {
  const [busy, setBusy] = useState(false);
  const [audio, setAudio] = useState(null);
  const [err, setErr] = useState("");
  const play = async () => {
    setBusy(true); setErr("");
    try {
      const r = await http.post(`/reader/read-aloud/${articleId}`, null, token ? { headers: { Authorization: `Bearer ${token}` } } : {});
      if (r.data?.audio_base64) {
        const url = `data:audio/mpeg;base64,${r.data.audio_base64}`;
        setAudio(url);
        const audioEl = new Audio(url);
        audioEl.play();
      } else if (r.data?.reason === "elevenlabs_key_missing") {
        setErr("Voice playback is being set up. Your reader is preparing.");
      } else if (r.data?.reason === "daily_cap_reached") {
        setErr("Daily TTS cap reached site-wide. Try tomorrow.");
      } else {
        setErr("Audio unavailable right now.");
      }
    } catch (ex) {
      setErr(ex?.response?.data?.detail || "Audio unavailable.");
    } finally { setBusy(false); }
  };
  return (
    <div>
      <button onClick={play} disabled={busy} className="btn icon w-full justify-center" data-testid={`read-aloud-${articleId}`}>
        {busy ? <Loader2 size={13} className="animate-spin" /> : <Volume2 size={13} />} Read aloud {isReaderOrUp ? "" : "(preview)"}
      </button>
      {audio && <audio controls src={audio} className="w-full mt-2" />}
      {err && <div className="text-[11px] text-[var(--muted)] italic mt-1">{err}</div>}
    </div>
  );
}
